import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { forwardRef } from "react";

import { cn } from "@/lib/utils/cn";

const buttonVariants = cva(
  // Contenedor más redondeado (rounded-lg), transición suave de todo y un
  // "press" táctil sutil al hacer clic (active:scale). Las animaciones de hover
  // ricas son opt-in vía la prop `animacion`.
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:scale-[0.97] disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // Semánticas TONALES (ADR 047): fondo suave + texto fuerte + borde.
        // Theme-aware (los tokens se adaptan a claro/oscuro) y no compiten con
        // el destructivo rojo. `success` usa el verde dedicado (no el de marca).
        success:
          "border border-state-aprobado/30 bg-state-aprobado/10 text-state-aprobado hover:bg-state-aprobado/20",
        warning: "border border-warning/30 bg-warning/10 text-warning hover:bg-warning/20",
      },
      size: {
        xs: "h-7 gap-1.5 px-2 text-xs", // toolbars / acciones densas (comentarios)
        sm: "h-9 px-3",
        default: "h-10 px-4 py-2",
        lg: "h-11 px-8",
        icon: "h-10 w-10",
      },
      /**
       * Animación de hover (opt-in). Todas son ON-BRAND: usan `--ring` (el color
       * principal del tenant), así el efecto sigue el branding y el tema. Las
       * clases viven en `globals.css` (respetan `prefers-reduced-motion`).
       */
      animacion: {
        none: "",
        elevar: "btn-elevar", // se levanta + sombra de marca
        brillo: "btn-brillo", // halo de marca alrededor
        destello: "btn-destello", // barrido de brillo que cruza el botón
      },
    },
    defaultVariants: { variant: "default", size: "default", animacion: "none" },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * Estado de carga: muestra un spinner centrado (preservando el ancho) y
   * desactiva el botón. Reemplaza el patrón hand-rolled de
   * `disabled={isPending}` + `{isPending ? <Loader2/> : …}`. No aplica con
   * `asChild` (los enlaces no se desactivan).
   */
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      animacion,
      asChild = false,
      loading = false,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const enCarga = loading && !asChild;
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, animacion, className }),
          enCarga && "relative",
        )}
        ref={ref}
        disabled={asChild ? disabled : disabled || loading}
        aria-busy={enCarga || undefined}
        {...props}
      >
        {enCarga ? (
          <>
            <span className="absolute inset-0 grid place-items-center" aria-hidden="true">
              <Loader2 className="h-4 w-4 animate-spin" />
            </span>
            {/* Contenido invisible que conserva el ancho mientras carga. */}
            <span className="inline-flex items-center gap-2 opacity-0">{children}</span>
          </>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { buttonVariants };
